import React from 'react';
import _ = require('underscore');
import { dataDrivenAlertNotificationType, sendReturnFromSlackTelemetry } from '~src/app/spaces/IntegrationsSettings/slackConnectivity/SlackIntegrationSettingsTelemetry';
import { UrlParamKeys } from '~src/app/UrlParamKeys';
import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';

import { Messages } from '../../codegen/Localize';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { UserErrorCode } from '../../ts/helpers/ErrorCodes';
import { Set } from '../../ts/models/Set';
import { DataAlerts } from '../resources/DataAlerts';
import { AddSelfToAlertsAction } from '../services/AddSelfToAlertsAction';
import { RemoveSelfFromAlertsAction } from '../services/RemoveSelfFromAlertsAction';
import { AddOrRemoveDataAlertComponent } from './AddOrRemoveFromDataAlertComponent';


export type AddOrRemoveFromDataAlertProviderProps = {
  readonly dataAlertId: string;
}

export function AddUserToDataAlertProvider(props: AddOrRemoveFromDataAlertProviderProps): JSX.Element {
  const [errorOccurred, setErrorOccurred] = React.useState(false);
  const [message, setMessage] = React.useState(undefined);
  const [dataAlertInfo, setDataAlertInfo] = React.useState(undefined);

  React.useEffect(
    () => {
      let isMounted = true;
      DataAlerts.getById(props.dataAlertId)
        .then(dataAlertInfo => {
          if (isMounted) {
            setDataAlertInfo(dataAlertInfo);
            if (dataAlertInfo.isCurrentUserSubscribed) {
              setMessage(Messages.addSelfToAlertsErrorAlreadyAdded({ ALERT_NAME: dataAlertInfo.name }));
            } else {
              const currentUser = getSessionManagementServiceInstance().sessionUserInfo();
              DataAlerts.addUsers({ dataAlertIds: [ dataAlertInfo.id ], userIds: [ currentUser.id ] })
                .then(result => isMounted ? setStateAfterAdd(result, dataAlertInfo.name) : null)
                .catch(() => isMounted ? setStateForErrorAddingUser() : null);
            }
          }
        })
        .catch(() => isMounted ? setStateForErrorGettingAlert() : null);

      return () => isMounted = false;
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const setStateForErrorAddingUser = (): void => {
    setErrorOccurred(true);
    setMessage(Messages.errorUnknown());
  };

  const setStateForErrorGettingAlert = () => {
    setErrorOccurred(true);
    setMessage(Messages.errorDataAlertNotFoundNoName());
  };

  const setStateAfterAdd = (result: IResult, dataAlertName: string): void => {
    if (result.errors && result.errors.length > 0) {
      setErrorOccurred(true);
      switch (result.errors[0].code) {
        case  UserErrorCode.UserInvalidEmailAddress:
          setMessage(Messages.addSelfToAlertsErrorNoEmailAddress({ ALERT_NAME: dataAlertName }));
        default:
          setMessage(Messages.addSelfToAlertThroughEmailError({ ALERT_NAME: dataAlertName }));
      }
    } else {
      setMessage(Messages.addSelfToAlertThroughEmailSuccess({ ALERT_NAME: dataAlertName }));
    }
  };

  return <AddOrRemoveDataAlertComponent
    errorOccurred={errorOccurred}
    message={message}
    undoAction={() => RemoveSelfFromAlertsAction.removeSelfFromAlert(Set.singletonForIdItem(dataAlertInfo))}
    undoButtonText={Messages.addSelfToAlertThroughEmailRemoveMe()} />;
}

export function RemoveUserFromDataAlertProvider(props: AddOrRemoveFromDataAlertProviderProps): JSX.Element {
  const [errorOccurred, setErrorOccurred] = React.useState(false);
  const [message, setMessage] = React.useState(undefined);
  const [dataAlertInfo, setDataAlertInfo] = React.useState(undefined);

  React.useEffect(
    () => {
      let isMounted = true;
      sendTelemetryIfLinkFromSlack();
      DataAlerts.getById(props.dataAlertId)
        .then(dataAlertInfo => {
          if (isMounted) {
            setDataAlertInfo(dataAlertInfo);
            if (!dataAlertInfo.isCurrentUserSubscribed) {
              setMessage(Messages.removeSelfFromDataAlertsErrorAlreadyRemoved({ ALERT_NAME: dataAlertInfo.name }));
            } else {
              const currentUser = getSessionManagementServiceInstance().sessionUserInfo();
              DataAlerts.removeUsers({ dataAlertIds: [ dataAlertInfo.id ], userIds: [ currentUser.id ] })
                .then(result => isMounted ? setStateAfterRemove(result, dataAlertInfo.name) : null)
                .catch(() => isMounted ? setStateForErrorRemovingUser() : null);
            }
          }
        })
        .catch(() => isMounted ? setStateForErrorGettingAlert() : null);

      return () => isMounted = false;
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const setStateForErrorRemovingUser = (): void => {
    setErrorOccurred(true);
    setMessage(Messages.errorUnknown());
  };

  const setStateForErrorGettingAlert = (): void => {
    setErrorOccurred(true);
    setMessage(Messages.errorDataAlertNotFoundNoName());
  };

  const sendTelemetryIfLinkFromSlack = () => {
    let sessionManagementServiceInstance = getSessionManagementServiceInstance();
    const search: any = WindowLocationService.getHashQueryObject();
    if (_.has(search, UrlParamKeys.LinkFromSlack)) {
      let slackLinkType = search[UrlParamKeys.LinkFromSlack];
      WindowLocationService.deleteQueryParameter(UrlParamKeys.LinkFromSlack);
      sendReturnFromSlackTelemetry({
        userId: sessionManagementServiceInstance.sessionUserId(),
        siteLuid: sessionManagementServiceInstance.sessionSiteLuid(),
        notificationType: dataDrivenAlertNotificationType,
        linkType: slackLinkType
      });
    }
  };

  const setStateAfterRemove = (result: IResult, dataAlertName: string): void => {
    if (result.errors && result.errors.length > 0) {
      setErrorOccurred(true);
      setMessage(Messages.removeSelfFromDataAlertThroughEmailError({ ALERT_NAME: dataAlertName }));
    } else {
      setMessage(Messages.removeSelfFromDataAlertThroughEmailSuccess({ ALERT_NAME: dataAlertName }));
    }
  };

  return <AddOrRemoveDataAlertComponent
    errorOccurred={errorOccurred}
    message={message}
    undoAction={() => AddSelfToAlertsAction.addSelfToAlert(Set.singletonForIdItem(dataAlertInfo))}
    undoButtonText={Messages.removeSelfFromDataAlertThroughEmailAddMeBack()} />;
}
